import { useMutation, useApolloClient } from '@apollo/client';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { omit } from 'ramda';
import { UserRewardStatus } from '../../constants';
import { useCountry } from '../../components/CountrySelector';
import { UseUserQuery } from '../../generated/types/UseUserQuery';
import { useUser, USER_QUERY } from '../useUser';
import { useOrderSummary } from '../useOrderSummary';
import { ENROLL_MUTATION, EnrollMutation, EnrollMutationVariables } from './useEnrollMutation.rest';

export const useEnrollMutation = () => {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const { orderSummary } = useOrderSummary(orderNumber);
  const client = useApolloClient();
  const { country } = useCountry();

  const { setUserData } = useUser();
  const [mutate, { loading, error }] = useMutation<EnrollMutation, EnrollMutationVariables>(
    ENROLL_MUTATION
  );

  const handleEnroll = useCallback(async () => {
    const {
      data: { user }
    } = await client.query<UseUserQuery>({ query: USER_QUERY });

    if (!user) return;

    if (user.id) {
      await mutate({
        variables: {
          id: user.id,
          input: {
            data: {
              type: 'users',
              attributes: {
                country_code: country.alpha2Code,
                enroll_to_loyalty: true,
                order_id: orderSummary && orderSummary.id
              },
              id: user.id
            }
          }
        }
      });
      setUserData({
        ...omit(['__typename'], user),
        userLoyaltyStatus: UserRewardStatus.Enrolled
      });

      return Promise.resolve();
    }

    return Promise.reject('User id not found.');
  }, [orderSummary, client, country.alpha2Code, mutate, setUserData]);

  return { handleEnroll, loading, error };
};
